import { useSyncExternalStore } from 'react';

import { send } from '@actual-app/core/platform/client/connection';
import { q } from '@actual-app/core/shared/query';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { aqlQuery } from '#queries/aqlQuery';

type Suggestion = {
  categoryId: string;
  confidence: number;
  source: 'knn' | 'llm';
};

const recommendations = new Map<string, Suggestion>();
const listeners = new Set<() => void>();

export type AiRecommendationItem = {
  transactionId: string;
  date: string;
  payeeName: string;
  amount: number;
  categoryId: string;
  categoryName: string;
  confidence: number;
  source: 'knn' | 'llm';
};

export function getRecommendation(
  transactionId: string,
): Suggestion | undefined {
  return recommendations.get(transactionId);
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

// reactive hook - re-renders the calling component when emit() fires
export function useCategoryRecommendation(
  transactionId: string,
): string | undefined {
  return useSyncExternalStore(
    subscribe,
    () => getRecommendation(transactionId)?.categoryId,
  );
}

function emit() {
  listeners.forEach(cb => cb());
}

function buildText(
  payeeName: string,
  notes: string | null | undefined,
  amount: number,
): string {
  const dir = amount < 0 ? 'outflow' : 'inflow';
  const money = integerToCurrency(Math.abs(amount));
  return `${payeeName} ${notes ?? ''} | ${dir} ${money}`.trim();
}

export async function requestRecommendations(
  accountId?: string,
): Promise<{ unavailable: boolean; items: AiRecommendationItem[] }> {
  const payees = (await send('payees-get')) as Array<{
    id: string;
    name: string;
  }>;
  const payeeName = new Map(payees.map(p => [p.id, p.name]));

  const categoriesResp = (await send('get-categories')) as {
    list: Array<{ id: string; name: string; is_income?: boolean }>;
  };
  const categories = categoriesResp.list
    .filter(c => c.name.trim().toLowerCase() !== 'starting balances')
    .map(c => ({ id: c.id, name: c.name }));
  const categoryName = new Map(categories.map(c => [c.id, c.name]));
  const incomeCategoryIds = new Set(
    categoriesResp.list.filter(c => c.is_income).map(c => c.id),
  );

  // fetch accounts for transfer-detection names + off-budget exclusion
  const accounts = (
    await aqlQuery(q('accounts').select(['id', 'name', 'offbudget']))
  ).data as Array<{ id: string; name: string; offbudget: boolean }>;
  const onBudgetAccountIds = new Set(
    accounts.filter(a => !a.offbudget).map(a => a.id),
  );
  const accountNames = accounts.map(a => a.name).filter(Boolean);

  const baseFilter: Record<string, unknown> = {
    category: null,
    transfer_id: null,
    is_parent: false,
  };
  if (accountId) baseFilter.account = accountId;

  const uncategorized = (
    await aqlQuery(
      q('transactions')
        .filter(baseFilter)
        .select(['id', 'date', 'payee', 'notes', 'amount', 'account']),
    )
  ).data as Array<{
    id: string;
    date: string;
    payee: string | null;
    notes?: string | null;
    amount: number;
    account: string;
  }>;

  const history = (
    await aqlQuery(
      q('transactions')
        .filter({
          category: { $ne: null },
          starting_balance_flag: false,
          transfer_id: null,
        })
        .select(['payee', 'notes', 'amount', 'category', 'account'])
        .orderBy({ date: 'desc' })
        .limit(2000),
    )
  ).data as Array<{
    payee: string | null;
    notes?: string | null;
    amount: number;
    category: string;
    account: string;
  }>;

  // exclude off-budget transactions from both sets
  const onBudgetUncategorized = uncategorized.filter(t =>
    onBudgetAccountIds.has(t.account),
  );
  const onBudgetHistory = history.filter(h =>
    onBudgetAccountIds.has(h.account),
  );

  const toClassify = onBudgetUncategorized.map(t => ({
    id: t.id,
    text: buildText(payeeName.get(t.payee ?? '') ?? '', t.notes, t.amount),
    amount: t.amount,
  }));
  const historyPayload = onBudgetHistory
    .map(h => ({
      text: buildText(payeeName.get(h.payee ?? '') ?? '', h.notes, h.amount),
      category: categoryName.get(h.category) ?? '',
      amount: h.amount,
    }))
    .filter(h => h.text && h.category);

  let raw: unknown;
  try {
    raw = await send('enablebanking-ai-categorize', {
      toClassify,
      history: historyPayload,
      categories,
      accountNames,
    });
  } catch {
    return { unavailable: true, items: [] };
  }

  // guard against { error: 'unauthorized' }, missing fields, or any unexpected shape
  if (
    !raw ||
    typeof raw !== 'object' ||
    'error' in raw ||
    (raw as { unavailable?: unknown }).unavailable === true ||
    !Array.isArray((raw as { suggestions?: unknown }).suggestions)
  ) {
    return { unavailable: true, items: [] };
  }

  const result = raw as {
    suggestions: Array<{ transactionId: string } & Suggestion>;
  };

  // build an amount lookup so we can apply the income-on-outflow guard
  const amountById = new Map(onBudgetUncategorized.map(t => [t.id, t.amount]));
  // build a lookup from uncategorized rows so we can enrich each suggestion
  const uncategorizedById = new Map(onBudgetUncategorized.map(t => [t.id, t]));

  recommendations.clear();
  const items: AiRecommendationItem[] = [];

  for (const s of result.suggestions) {
    const txAmount = amountById.get(s.transactionId);
    // never recommend an income category for an outflow
    if (
      txAmount !== undefined &&
      txAmount < 0 &&
      incomeCategoryIds.has(s.categoryId)
    ) {
      continue;
    }

    recommendations.set(s.transactionId, {
      categoryId: s.categoryId,
      confidence: s.confidence,
      source: s.source,
    });

    const tx = uncategorizedById.get(s.transactionId);
    const catName = categoryName.get(s.categoryId);
    if (tx && catName) {
      items.push({
        transactionId: s.transactionId,
        date: tx.date,
        payeeName: payeeName.get(tx.payee ?? '') ?? '',
        amount: tx.amount,
        categoryId: s.categoryId,
        categoryName: catName,
        confidence: s.confidence,
        source: s.source,
      });
    }
  }

  emit();
  return { unavailable: false, items };
}

// Wipe the server-side learned data (the embedding cache). Optionally scope to a
// single category. The cache rebuilds from the user's categorized history on the
// next pass. Also clears the in-memory recommendation cache so stale picker
// suggestions do not linger.
export async function resetLearnedData(category?: string): Promise<number> {
  const raw = (await send(
    'enablebanking-ai-categorize-reset',
    category ? { category } : {},
  )) as { reset?: boolean; removed?: number; error?: string };
  if (!raw || raw.error || raw.reset !== true) {
    throw new Error(raw?.error ?? 'reset failed');
  }
  recommendations.clear();
  emit();
  return raw.removed ?? 0;
}
